/* eslint-disable @angular-eslint/no-output-native */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import FroalaEditor from 'froala-editor';

import { AwsService, ConfigurationService, SpinnerService, ToastService } from '@reg/shared/service';

declare let $: any;

@Component({
    selector: 'flo-html-editor',
    templateUrl: './html-editor.component.html',
})
export class HtmlEditorComponent implements OnInit {
    @Output() public contentChange: EventEmitter<string> = new EventEmitter<string>();
    @Output() public blur: EventEmitter<void> = new EventEmitter<void>();
    @Input() public content: string;
    private _uploadFolderName: string;
    @Input()
    set uploadFolderName(uploadFolderName: string) {
        this._uploadFolderName = uploadFolderName;
    }

    get uploadFolderName(): string {
        return this._uploadFolderName;
    }

    private _disabled: boolean = false;
    @Input()
    set disabled(disabled: boolean) {
        if (this.froalaEditor &&
            this._disabled !== disabled) {
            if (disabled) {
                this.froalaEditor.edit.off();
            } else {
                this.froalaEditor.edit.on();
            }
        }

        this._disabled = disabled;
    }

    get disabled(): boolean {
        return this._disabled;
    }

    private froalaEditor: any;
    private froalaEditorOptions: any;
    private froalaInitControls: any;

    private _isfroalaEditorProcessing: boolean;

    set isFroalaEditorProcessing(isProcessing: boolean) {
        this._isfroalaEditorProcessing = isProcessing;

        if (isProcessing) {
            this.spinnerService.show();
        } else {
            this.spinnerService.hide();
        }
    }

    get isFroalaEditorProcessing(): boolean {
        return this._isfroalaEditorProcessing;
    }

    constructor(
                private toastService: ToastService,
                private configService: ConfigurationService,
                private awsService: AwsService,
                private spinnerService: SpinnerService,
    ) {
    }

    public ngOnInit(): void {
        this.getEditorOptions();
    }

    public setContent(content: string): void {
        if (this.froalaEditor) {
            this.froalaEditor.html.set(content);
        }
    }

    public getEditorOptions(): Object {
        if (this.froalaEditorOptions) {
            return this.froalaEditorOptions;
        }

        const toolbarButtons: string[] = [
            'bold',
            'italic',
            'underline',
            'strikeThrough',
            'fontFamily',
            'fontSize',
            'textColor',
            'backgroundColor',
            '|',
            'align',
            'formatOL',
            'formatUL',
            'indent',
            'outdent',
            '|',
            'insertHR',
            'insertLink',
            'insertImage',
            'insertTable',
            '-',
            'undo',
            'redo',
            'clearFormatting',
            'selectAll',
            'html',
        ];

        const maxImageSizeInBytes: number = 1024 * 1024 * 2; // 2MB

        this.froalaEditorOptions = {
            key: this.configService.getBaseConfig('froalaEditorLicenseKey'),
            placeholderText: 'Enter event details (ticket info, parking, awards, etc)',
            heightMin: 100,
            heightMax: 300,
            toolbarSticky: false,
            theme: 'gray',
            enter: FroalaEditor.ENTER_BR,
            linkAlwaysBlank: true,
            attribution: false,
            toolbarButtons: toolbarButtons,
            toolbarButtonsMD: toolbarButtons,
            toolbarButtonsSM: toolbarButtons,
            toolbarButtonsXS: toolbarButtons,
            imageMaxSize: maxImageSizeInBytes,
            imageDefaultWidth: 0, // default to the image width
            events: {
                initialized: (e: any, editor: any): void => {
                    this.froalaEditor = e.getEditor();
                    this.froalaEditor.html.set(this.content);

                    this.awsService.getS3UploadHash(this.uploadFolderName).subscribe((s3Hash: any) => {
                        this.froalaEditor.opts.imageUploadToS3 = s3Hash;
                    });
                },
                'image.beforeUpload': (e: any, files: any): void => {
                    this.toastService.clear();
                    this.isFroalaEditorProcessing = true;
                },
                'image.inserted': (e: any, link: any, key: any, response: any): void => {
                    this.isFroalaEditorProcessing = false;
                    this.contentChange.emit(this.froalaEditor.html.get());
                },
                'image.replaced': (e: any, link: any, key: any, response: any): void => {
                    this.isFroalaEditorProcessing = false;
                    this.contentChange.emit(this.froalaEditor.html.get());
                },
                'image.error': (e: any, error: any): void => {
                    this.isFroalaEditorProcessing = false;
                    this.toastService.showError(error.message);
                    console.error(error);
                },
                'image.resize': (e: any, editor: any, $img: any): void => {
                    this.contentChange.emit(this.froalaEditor.html.get());
                },
                blur: (e: any, error: any): void => {
                    if (!this.isFroalaEditorProcessing) {
                        // Trigger content change to resolve a bug related to selecting all text, delete and then the user navigates
                        this.contentChange.emit(this.froalaEditor.html.get());
                        this.blur.emit();
                    }
                },
            },
        };

        return this.froalaEditorOptions;
    }
}
